import { lazy, Suspense } from "react";
import { Link } from "react-router-dom";
import { formatUtcDateTime } from "../lib/timeDisplay";
import { formatMarketTotalVotes } from "../lib/marketTokenDisplay.js";
import { supportCommunityNameForMarket } from "../lib/supportCommunities";
import { useWalletAuth } from "../lib/walletAuth";
import { genesisMarketBadgeLabel, genesisMarketBadgeDescription } from "../lib/genesisMarket";
import { MarketAvatar } from "./MarketAvatar";
import { MarketExternalLinks } from "./MarketExternalLinks";
import { MarketShareButton } from "./MarketShareButton";
import { MarketCardVotePanelSkeleton } from "./skeletons";

const VotePanel = lazy(() => import("./VotePanel").then((module) => ({ default: module.VotePanel })));

export function MarketCard({ market, className = "", imagePriority = false }) {
  const { session } = useWalletAuth();
  const affiliateId = session?.walletAddress || session?.userId || "";
  const genesisBadgeLabel = genesisMarketBadgeLabel(market);
  const genesisBadgeDescription = genesisMarketBadgeDescription(market);

  return (
    <article
      className={["route-shell flex min-w-0 w-full flex-col gap-4", className].join(" ").trim()}
      data-cls-measure="market-card"
    >
      <header className="space-y-3">
        <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
          <div className="flex min-w-0 items-start gap-2">
            <MarketAvatar imageUrl={market.imageUrl} title={market.title} priority={imagePriority} />
            <h2 className="min-w-0 text-lg font-semibold text-zinc-100">
              <Link to={`/market/${market.id}`} className="hover:text-blue-300">
                {market.title}
              </Link>
            </h2>
          </div>
          <div className="flex flex-wrap items-center gap-2 md:justify-end">
            {genesisBadgeLabel ? (
              <span
                className="rounded-full border border-cyan-300/40 bg-cyan-400/10 px-2 py-1 text-center text-xs text-cyan-300"
              title={genesisBadgeDescription || genesisBadgeLabel}
            >
              Genesis only
              </span>
            ) : null}
            <span className="rounded-full border border-blue-500/40 px-2 py-1 text-center text-xs text-blue-400">
              {formatMarketTotalVotes(market)}
            </span>
            <MarketShareButton market={market} affiliateId={affiliateId} />
          </div>
        </div>
        <p className="text-sm text-zinc-300">{market.summary}</p>
        <p className="text-xs text-zinc-400">This market supports {supportCommunityNameForMarket(market)}.</p>
        <MarketExternalLinks xUrl={market.xUrl} websiteUrl={market.websiteUrl} />
      </header>

      <Suspense fallback={<MarketCardVotePanelSkeleton />}>
        <VotePanel market={market} />
      </Suspense>

      <footer className="flex flex-col gap-1 border-t border-zinc-800 pt-3 text-xs text-zinc-400 md:flex-row md:items-center md:justify-between">
        <span>Ends (UTC) {formatUtcDateTime(market.endAt)}</span>
        <Link to={`/market/${market.id}`} className="text-blue-400 hover:text-blue-300">
          View detail
        </Link>
      </footer>
    </article>
  );
}
